import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Play, ArrowRight, Star, Music2, Heart, Users, Volume2, VolumeX } from "lucide-react";
import Reveal from "../components/Reveal";
import ExperienceForm from "../components/ExperienceForm";
import { IMAGES } from "../mock/mock";

const Home = () => {
  const [muted, setMuted] = useState(true);
  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center justify-center text-center px-5 pt-24 overflow-hidden">
        {/* fallback poster + background video */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.heroBand})` }}
        />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <iframe
            key={muted ? "muted" : "unmuted"}
            title="A.T.O.M. background performance"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.77vh] min-w-full h-[56.25vw] min-h-full"
            src={`https://www.youtube.com/embed/d4dQ3yRfo9Y?autoplay=1&mute=${muted ? 1 : 0}&loop=1&playlist=d4dQ3yRfo9Y&controls=0&showinfo=0&modestbranding=1&rel=0&playsinline=1&disablekb=1&iv_load_policy=3&cc_load_policy=0&fs=0`}
            frameBorder="0"
            allow="autoplay; encrypted-media"
          />
        </div>
        {/* dark overlays for readability */}
        <div className="absolute inset-0 bg-[#0c0a08]/40 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0c0a08]/90 via-[#0c0a08]/20 to-[#0c0a08]/90 pointer-events-none" />
        {/* sound toggle */}
        <button
          onClick={() => setMuted((m) => !m)}
          aria-label={muted ? "Unmute video" : "Mute video"}
          className="absolute bottom-6 right-6 z-20 w-12 h-12 rounded-full border border-[#e0b04c]/40 bg-[#0c0a08]/60 backdrop-blur-md flex items-center justify-center text-[#e0b04c] hover:bg-[#e0b04c] hover:text-[#0c0a08] transition-all duration-300"
        >
          {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
        </button>
        {/* ambient glow */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#e0b04c]/10 rounded-full blur-[140px] pointer-events-none" />
        <div className="relative z-10 max-w-3xl mx-auto animate-fade-in-up">
          <p className="text-[#e0b04c] text-sm tracking-[0.35em] uppercase font-medium mb-6">
            Experience the Legacy
          </p>
          <h1 className="font-serif-display text-7xl md:text-8xl font-bold text-gold-gradient mb-5">
            A.T.O.M.
          </h1>
          <h2 className="font-serif-display text-3xl md:text-4xl text-[#f5f0e8] mb-7">
            A Tribute to Frankie Beverly &amp; Maze
          </h2>
          <p className="text-[#b3a998] text-lg leading-relaxed max-w-2xl mx-auto mb-10">
            Faithfully honoring the music, the spirit, and the timeless joy that
            has moved generations. A full-band, concert-ready experience that
            celebrates love, community, and soul.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/booking"
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-b from-[#e8bc55] to-[#c99339] text-[#0c0a08] font-semibold px-8 py-4 rounded-md hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(224,176,76,0.4)] transition-all duration-300"
            >
              <Calendar size={18} /> Book A.T.O.M.
            </Link>
            <Link
              to="/media"
              className="inline-flex items-center justify-center gap-2 border border-[#e0b04c]/35 text-[#f5f0e8] font-semibold px-8 py-4 rounded-md hover:bg-[#e0b04c]/10 hover:border-[#e0b04c] transition-all duration-300"
            >
              <Play size={18} /> Watch Live Performance
            </Link>
          </div>
        </div>
        {/* scroll hint */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 w-6 h-10 rounded-full border-2 border-[#e0b04c]/40 flex justify-center pt-2">
          <div className="w-1.5 h-1.5 rounded-full bg-[#e0b04c] animate-float-slow" />
        </div>
      </section>

      {/* STATS BAR */}
      <section className="border-y border-[#e0b04c]/12 bg-[#0a0806]/60">
        <div className="max-w-6xl mx-auto px-5 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/5">
          {[
            { n: "50+", l: "Years of Legacy" },
            { n: "100%", l: "Live Full Band" },
            { n: "20+", l: "Signature Songs" },
            { n: "\u221E", l: "Joy Delivered" },
          ].map((s, i) => (
            <Reveal key={i} delay={i * 100} className="py-10 text-center px-4">
              <div className="font-serif-display text-4xl md:text-5xl text-gold-gradient mb-1">
                {s.n}
              </div>
              <div className="text-[#8f8577] text-xs uppercase tracking-widest">
                {s.l}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="py-28 px-5">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-14 items-center">
          <Reveal className="relative group">
            <div className="absolute -inset-3 bg-gradient-to-tr from-[#e0b04c]/20 to-transparent rounded-xl blur-xl opacity-60 group-hover:opacity-100 transition-opacity duration-500" />
            <img
              src={IMAGES.bandGroupWhite}
              alt="The A.T.O.M. band dressed in white"
              className="relative rounded-xl w-full object-cover shadow-2xl group-hover:scale-[1.02] transition-transform duration-700"
            />
          </Reveal>
          <Reveal delay={150}>
            <p className="text-[#e0b04c] text-sm tracking-[0.3em] uppercase font-medium mb-4">
              About The Band
            </p>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#f5f0e8] mb-6 leading-tight">
              More Than a Tribute. <span className="text-gold-gradient">A Celebration.</span>
            </h2>
            <p className="text-[#b3a998] leading-relaxed mb-5">
              A.T.O.M. was born from a deep love and respect for the music of
              Frankie Beverly &amp; Maze—songs that have soundtracked weddings,
              reunions, and life's most meaningful moments for over four decades.
            </p>
            <p className="text-[#b3a998] leading-relaxed mb-8">
              We are seasoned musicians who understand that this music is more
              than entertainment. It's a feeling. It's community. It's joy that
              brings strangers together as family.
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 text-[#e0b04c] font-semibold border border-[#e0b04c]/35 px-6 py-3 rounded-md hover:bg-[#e0b04c]/10 hover:gap-3 transition-all duration-300"
            >
              Learn Our Story <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-20 px-5 bg-[#0a0806]/60 border-y border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Music2, t: "Authentic Sound", d: "No backing tracks. Real soul, R&B, and funk musicianship on every note." },
              { icon: Heart, t: "Deep Respect", d: "Honoring the 50+ year legacy of Frankie Beverly & Maze with every performance." },
              { icon: Users, t: "Pure Connection", d: "We turn strangers into family through dancing, singing, and shared joy." },
            ].map((v, i) => (
              <Reveal key={i} delay={i * 120}>
                <div className="h-full p-8 rounded-xl bg-[#12100c] border border-white/5 hover:border-[#e0b04c]/30 hover:-translate-y-1 transition-all duration-400 group">
                  <div className="w-14 h-14 rounded-lg bg-[#e0b04c]/10 flex items-center justify-center mb-5 group-hover:bg-[#e0b04c]/20 transition-colors">
                    <v.icon className="text-[#e0b04c]" size={26} />
                  </div>
                  <h3 className="font-serif-display text-2xl text-[#f5f0e8] mb-3">{v.t}</h3>
                  <p className="text-[#9c917f] leading-relaxed">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BOOKING CTA */}
      <section className="py-28 px-5 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#e0b04c]/5 to-transparent pointer-events-none" />
        <Reveal className="max-w-3xl mx-auto text-center relative z-10">
          <p className="text-[#e0b04c] text-sm tracking-[0.3em] uppercase font-medium mb-4">
            Ready to Feel the Music?
          </p>
          <h2 className="font-serif-display text-4xl md:text-6xl text-[#f5f0e8] mb-6 leading-tight">
            Bring the Magic of Maze to <span className="text-gold-gradient">Your Event</span>
          </h2>
          <p className="text-[#b3a998] text-lg leading-relaxed mb-10">
            Whether you're planning a festival, corporate gala, theater
            performance, or private celebration, A.T.O.M. delivers a
            concert-quality experience that will have your audience dancing,
            singing, and feeling the joy.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/booking" className="bg-gradient-to-b from-[#e8bc55] to-[#c99339] text-[#0c0a08] font-semibold px-8 py-4 rounded-md hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(224,176,76,0.4)] transition-all duration-300">
              Request Availability
            </Link>
            <Link to="/contact" className="border border-[#e0b04c]/35 text-[#f5f0e8] font-semibold px-8 py-4 rounded-md hover:bg-[#e0b04c]/10 hover:border-[#e0b04c] transition-all duration-300">
              Contact Us
            </Link>
          </div>
        </Reveal>
      </section>

      {/* EXPERIENCE / TESTIMONIAL FORM */}
      <section className="py-24 px-5 bg-[#0a0806]/60 border-t border-white/5">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="text-[#e0b04c] text-sm tracking-[0.3em] uppercase font-medium mb-4">
              What They Say
            </p>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#f5f0e8] mb-6 leading-tight">
              Share Your <span className="text-gold-gradient">Experience</span>
            </h2>
            <p className="text-[#b3a998] leading-relaxed mb-6">
              Every show is about energy, memories, connection, and celebration.
              We love hearing how our performances made you feel. Share your
              experience with the A.T.O.M. family.
            </p>
            <div className="flex items-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={22} className="fill-[#e0b04c] text-[#e0b04c]" />
              ))}
            </div>
            <img src={IMAGES.allWhiteTribute} alt="A.T.O.M. all-white tribute" className="rounded-xl w-full object-cover mt-6 shadow-xl" />
          </Reveal>
          <Reveal delay={150}>
            <ExperienceForm />
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
